import t from 'react-translate';
import moment from 'moment';
import {
  MouseEventHandler,
  useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';

// redux
import { getAiRolePlay, getAiRolePlaySubmission } from 'redux/selectors/timeline';

import NvTooltip from 'shared/components/nv-tooltip';
import { AngularContext } from 'react-app';
import { config } from '../../../../config/pendo.config.json';
import { LecturePageMode } from '..';

type SubmitSectionProps = {
  aiRolePlayId: number;
  handleOpenRolePlay: MouseEventHandler<HTMLElement>;
  mode: LecturePageMode;
  isHardDeadlineExpired: boolean;
};

const SubmitSection = (props: SubmitSectionProps) => {
  const {
    aiRolePlayId,
    handleOpenRolePlay,
    mode,
    isHardDeadlineExpired,
  } = props;
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));
  const submission = useSelector((state) => getAiRolePlaySubmission(state, aiRolePlay?.submissionId));

  // View Feedback opens the same conversation modal as the start flow, but in
  // view-feedback mode (read-only conversation + feedback panel). Both flows
  // are now driven by the route ('role-play-submission-modal'), so we just
  // navigate — ConversationView (mounted at '/:submissionId') hydrates its
  // own state from the conversation API. The previous implementation lifted
  // messages/sessionData/openingNarrative/aiRolePlayStatus into the lecture
  // page index.tsx and toggled an in-page NvModal; that state is no longer
  // read by anyone in the route-driven flow, so we drop it here too.
  const handleViewFeedback = () => {
    if (!submission?.id) return;
    $state.go('role-play-submission-modal', {
      rolePlayId: aiRolePlay.id,
      section: String(submission.id),
    });
  };

  if (submission?.id) {
    return (
      <div className='d-flex  flex-column mt-6'>
        {submission?.finishedAt && (
          <p
            className='text-gray-3 font-weight-bold text-small'
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.LAST_COMPLETED_ON(moment(submission.finishedAt).format('LLL'))}
          </p>
        )}
        <div className='d-flex justify-content-center'>
          <NvTooltip
            text={t.EVALUATIONS.DEADLINE_PASSED()}
            enabled={isHardDeadlineExpired && mode === LecturePageMode.VIEW}
          >
            <Button
              variant='secondary'
              className='mr-1'
              disabled={mode !== LecturePageMode.VIEW || isHardDeadlineExpired}
              onClick={handleOpenRolePlay}
              data-qa={config.pendo.activities.aiRolePlay.retry}
            >
              {t.FORM.RETRY()}
            </Button>
          </NvTooltip>
          <Button
            variant='primary'
            className='ml-1'
            disabled={mode !== LecturePageMode.VIEW}
            onClick={handleViewFeedback}
            data-qa={config.pendo.activities.aiRolePlay.viewFeedback}
          >
            {t.EVALUATIONS.VIEW_FEEDBACK()}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='d-flex flex-column mt-6'>
      {isHardDeadlineExpired && (
        <p className='text-gray-3 font-weight-bold text-small'>
          {t.EVALUATIONS.DEADLINE_PASSED()}
        </p>
      )}
      <div className='d-flex justify-content-center'>
        <Button
          variant='primary'
          disabled={mode !== LecturePageMode.VIEW || isHardDeadlineExpired}
          onClick={handleOpenRolePlay}
          data-qa={config.pendo.activities.aiRolePlay.startRolePlay}
        >
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.START_AI_ROLE_PLAY()}
        </Button>
      </div>
    </div>
  );
};

export default SubmitSection;
